import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Button,
  Card,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CachedIcon from "@mui/icons-material/Cached";
import { useDropzone } from "react-dropzone";
import { enqueueSnackbar } from "notistack";
import { useTranslationHook } from "../../../hooks";
import { Procedure } from "../../../types/model/procedure";
import { Stage } from "../../../types/model/stage";
import { getStageFiles } from "../../../api/file/getStageFiles";
import { uploadFile } from "../../../api/file/uploadFile";
import { deleteFile } from "../../../api/file/deleteFile";
import { downloadFile } from "../../../api/file/downloadFile";
import { isProcedureManager } from "../../../api/procedure/isProcedureManager";
import { verifyTender } from "../../../api/procedure/verifyTender";
import UserAvatar from "../../Users/UserAvatar";
import { UploadFileRequest } from "../../../types/interfaces/file";

interface ReportProps {
  stage: Stage;
  accountId: number;
  userId: number;
  projectId: number;
  activityId: number;
  procedureId: number;
  procedure: Procedure;
}

const Report = ({
  stage,
  accountId,
  userId,
  projectId,
  activityId,
  procedureId,
  procedure,
}: ReportProps) => {
  const { t } = useTranslationHook();
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isManager, setIsManager] = useState(false);
  const [isTender, setIsTender] = useState(false);
  const [filteredFiles, setFilteredFiles] = useState<any[]>([]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => setPendingFiles((prev) => [...prev, ...files]),
    accept: {},
  });

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const [manager, tender] = await Promise.all([
          isProcedureManager(projectId, activityId, procedureId, userId),
          verifyTender(projectId, activityId, procedureId, accountId),
        ]);
        setIsManager(!!manager);
        setIsTender(!!tender);
      } catch {
        enqueueSnackbar("Erro ao verificar permissões", { variant: "error" });
      }
    };
    fetchRoles();
  }, [projectId, activityId, procedureId, userId, accountId]);

  const fetchExistingFiles = async () => {
    try {
      const files = await getStageFiles(stage.id);
      setUploadedFiles(files);
    } catch {
      enqueueSnackbar("Erro ao buscar ficheiros", { variant: "error" });
    }
  };

  useEffect(() => {
    fetchExistingFiles();
  }, []);

  useEffect(() => {
    const term = search.toLowerCase();
    const filtered = uploadedFiles.filter(
      (file) =>
        file.originalName?.toLowerCase().includes(term) ||
        (file.user?.name || "").toLowerCase().includes(term) ||
        (file.accountName || "").toLowerCase().includes(term)
    );
    setFilteredFiles(filtered);
    setPage(0);
  }, [search, uploadedFiles]);

  const handleUpload = async () => {
    try {
      await Promise.all(
        pendingFiles.map((file) => {
          const req: UploadFileRequest = {
            accountId,
            projectId,
            activityId,
            procedureId,
            stageId: stage.id,
            file,
          };
          return uploadFile(req);
        })
      );
      enqueueSnackbar("Ficheiros carregados", { variant: "success" });
      setPendingFiles([]);
      fetchExistingFiles();
    } catch {
      enqueueSnackbar("Erro ao carregar ficheiros", { variant: "error" });
    }
  };

  const handleDelete = async (fileId: number) => {
    try {
      await deleteFile(fileId);
      enqueueSnackbar("Ficheiro apagado", { variant: "success" });
      fetchExistingFiles();
    } catch {
      enqueueSnackbar("Erro ao apagar ficheiro", { variant: "error" });
    }
  };

  const handleDownload = async (fileId: number, filename: string) => {
    try {
      await downloadFile(fileId, filename);
    } catch {
      enqueueSnackbar("Erro ao fazer download", { variant: "error" });
    }
  };

  const filesToShow = isManager
    ? filteredFiles
    : filteredFiles.filter(
        (f) => f.accountId === accountId && f.createdBy === userId
      );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('generic.stage') + ' - ' + t(stage.stageTypeLabel)}
      </Typography>

      {(isManager || isTender) && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <TextField
              fullWidth
              label={t("generic.search") ?? "Search"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Tooltip title={t("generic.refresh")}>
              <IconButton onClick={fetchExistingFiles}>
                <CachedIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("generic.file-name")}</TableCell>
                  <TableCell>{t("generic.created")}</TableCell>
                  <TableCell>{t("generic.name")}</TableCell>
                  <TableCell>{t("generic.account")}</TableCell>
                  <TableCell align="right">{t("generic.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filesToShow
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((file) => (
                    <TableRow key={file.id}>
                      <TableCell>{file.filename}</TableCell>
                      <TableCell>{file.createdOn}</TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <UserAvatar
                            accountId={file.accountId}
                            userId={file.createdBy}
                            size={24}
                          />
                          <Typography variant="caption">
                            {file.user?.name || "Unknown"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {file.accountName || `#${file.accountId}`}
                      </TableCell>
                      <TableCell align="right">
                        {(isManager || isTender || stage.closed) && (
                        <IconButton
                          onClick={() => handleDownload(file.id, file.originalName)}
                          color="primary"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                            )}
                        {isTender && file.createdBy === userId && !procedure.canceled && !procedure.closed && (
                          <IconButton
                            onClick={() => handleDelete(file.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={filesToShow.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      {isTender && !procedure.canceled && !procedure.closed && (
        <>
          <Box
            {...getRootProps()}
            sx={{
              border: isDragActive ? "2px solid gray" : "2px dashed gray",
              borderRadius: 1,
              p: 3,
              mb: 2,
              textAlign: "center",
              cursor: "pointer",
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 60, color: "gray", mb: 1 }} />
            <Typography>
              {isDragActive
                ? t("generic.drop-here")
                : t("generic.drag-or-click")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
            {pendingFiles.map((file) => (
              <Card
                key={file.name}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1,
                  width: 250,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <InsertDriveFileIcon sx={{ color: "gray" }} />
                  <Typography>{file.name}</Typography>
                </Box>
                <IconButton
                  onClick={() =>
                    setPendingFiles(pendingFiles.filter((f) => f !== file))
                  }
                  color="error"
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Card>
            ))}
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={pendingFiles.length === 0 || procedure.closed}
          >
            {t("generic.upload")}
          </Button>
        </>
      )}
    </Box>
  );
};

export default Report;
